import { FC } from 'react'
import { GridLegacy as Grid, Typography, Box, Tooltip, IconButton } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from "react-i18next";

import { CSVDownloader } from '../../fileDownload/csv-downloader';
import { IChartProps } from '../common/types';
import { ITableOptions, isTableOptions, ITableData } from './table-options'
import { useUserParams } from '../../../context/user-params-context';
import { TableCell } from './table-cell';
import InfoIcon from '@mui/icons-material/Info';
import styles from './table.module.css';
import { useChartData } from '../../../hooks/useChartData';

const NoRowsOverlay = () => {
  const { t } = useTranslation()

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      alignItems: "center",
      justifyContent: "center"
    }}
    >
      <Typography>
        {t('charts.table.noData')}
      </Typography>
    </Box>
  )
}

export const Table: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation()
  const { userParams, parameterTypes } = useUserParams();

  const { data, isLoading } = useChartData<ITableData[], ITableOptions>(
    uuid,
    parameterTypes,
    userParams,
    {
      allowEmptyParams: true,
      validateOptions: isTableOptions,
      validateData: (data): data is ITableData[] => {
        // TODO: ITableData type is very vague, so we get the data casted as unknown. Please look into this.
        return Array.isArray(data) &&
          data.filter((item: ITableData) => item.id).length > 0;
      }
    }
  );

  const options = data?.options;
  const tableData = data?.data?.filter((item) => item.id) || [];
  const chartName = data?.name;

  const DEFAULT_COLUMN_WIDTH = 120;

  const columns: GridColDef[] = options?.columns
    ? Object.entries(options?.columns).map(([key, opts]) => ({
      headerName: opts.header,
      field: key,
      editable: false,
      sortable: opts.isSortable || false,
      filterable: opts.isFilterable || false,
      disableColumnMenu: !opts.hasMenu,
      width: opts.width || DEFAULT_COLUMN_WIDTH,
      flex: opts.flex,
      resizable: opts.isResizable || false,
      headerClassName: opts.isPinned ? "MuiDataGrid-pinned" : undefined,
      cellClassName: opts.isPinned ? "MuiDataGrid-pinned" : undefined,
      renderCell: (props) => <TableCell {...props} options={opts} />,
    }))
    : []

  const title = isLoading ? t("general.loading") : chartName || '';
  const tooltipText = options?.description?.startsWith('charts.')
    ? t(options.description)
    : options?.description;

  return (
    <Grid item xs={12} md={12} lg={12} className={styles.tableContainer}>
      <Typography
        variant="subtitle1"
        my={2}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        {!isLoading && options?.description ? (
          <div className={styles.titleContainer}>
            <span>{title}</span>
            <div className={styles.tableInfoTooltip}>
              <Tooltip title={tooltipText}>
                <IconButton
                  size="small"
                  className={styles.tableInfoIconButton}
                  aria-label={t('general.moreInfo')}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ) : (
          <span>{title}</span>
        )}
        {options?.enableCSVDownload ? (
          <CSVDownloader
            data={tableData}
            filename={chartName || ''}
            style={{ position: 'unset', display: 'inline-flex', margin: '0' }}
          />
        ) : ''}
      </Typography>
      <div style={{ height: options?.height || 400, width: '100%' }}>
        <DataGrid
          sx={{
            borderRadius: 'var(--radius)',
            border: '1px solid hsl(var(--border))',
            '& .MuiDataGrid-columnHeaders > div, .MuiDataGrid-columnHeader': {
              backgroundColor: 'hsl(var(--card)) !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
              whiteSpace: "normal",
              lineHeight: "normal"
            },
            '& .MuiDataGrid-pinned': {
              position: "sticky",
              left: 0,
              zIndex: 4,
              backgroundColor: 'hsl(var(--card))',
              fontWeight: 'bold',
              borderRight: '1px solid hsl(var(--border))'
            }
          }}
          columns={columns}
          rows={tableData}
          loading={isLoading}
          slots={{
            noRowsOverlay: NoRowsOverlay
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            }
          }}
          // We need to disable virtualisation if we have a pinned column
          // to prevent it being removed from the DOM when scrolled past
          disableVirtualization={Object.values(options?.columns || {}).some((c) => c.isPinned)}
        />
      </div>
    </Grid>
  )
}

export default Table;
